import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'
import { getEventListPageStrapiURL } from '~/utils/strapiURLhelpers'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'
import { useStrapiConfig, buildStrapiUrl } from '~/composables/useStrapi'
import { parseLocationSlug } from '~/composables/transactionalPage/useLocationSlugParser'
import { useDynamicContentMetrics } from '~/composables/useDynamicContentMetrics'

const FETCH_TIMEOUT_MS = 10_000

interface UseResolvedEventListPageParams {
  pageType: EventListPageTypeString
  slug?: string
  locationSlug?: string
  categorySlug?: string
  skipDynamicContent?: boolean
}

const PAGE_TYPE_TO_RESOURCE: Record<string, string> = {
  [EventListPageType.CITY]: 'Locality',
  [EventListPageType.CATEGORY]: 'Category',
  [EventListPageType.CITY_CATEGORY]: 'LocalityCategory',
  [EventListPageType.GENERAL]: 'Tag',
  [EventListPageType.TEAMEVENT]: 'Tag',
}

const LOCATION_TYPE_TO_METRICS_RESOURCE: Partial<
  Record<'region' | 'state' | 'neighborhood', string>
> = {
  region: 'Region',
}

const getMetricsRequest = (
  pageType: EventListPageTypeString,
  params: Pick<
    UseResolvedEventListPageParams,
    'locationSlug' | 'categorySlug' | 'slug'
  >
): { resource: string; slug: string } | null => {
  if (
    (pageType === EventListPageType.CITY ||
      pageType === EventListPageType.CITY_CATEGORY) &&
    params.locationSlug
  ) {
    const parsed = parseLocationSlug(params.locationSlug)
    if (!parsed.isCity) {
      const resource = LOCATION_TYPE_TO_METRICS_RESOURCE[parsed.locationType]
      if (!resource) return null

      if (pageType === EventListPageType.CITY_CATEGORY) return null

      return { resource, slug: parsed.readableName }
    }
  }

  const resource = PAGE_TYPE_TO_RESOURCE[pageType]
  const slugMap: Record<string, string | undefined> = {
    [EventListPageType.CITY]: params.locationSlug,
    [EventListPageType.CATEGORY]: params.categorySlug,
    [EventListPageType.CITY_CATEGORY]: `${params.locationSlug}-${params.categorySlug}`,
    [EventListPageType.GENERAL]: params.slug,
    [EventListPageType.TEAMEVENT]: params.slug,
  }
  const slug = slugMap[pageType]

  if (!resource || !slug) return null

  return { resource, slug }
}

export const useResolvedEventListPage = (
  params: UseResolvedEventListPageParams
) => {
  const { locale, locales } = useI18n()
  const route = useRoute()
  const strapiConfig = useStrapiConfig()
  const { fetchDynamicContentMetrics } = useDynamicContentMetrics()

  const fetchStrapiPages = async (
    localeCode: string
  ): Promise<StrapiEventListPage[]> => {
    const urlSuffix = getEventListPageStrapiURL(
      localeCode,
      params.slug,
      params.locationSlug,
      params.categorySlug
    )
    const url = buildStrapiUrl(strapiConfig, urlSuffix)

    try {
      const response = await $fetch<
        StrapiApiDefaultResponse<StrapiEventListPage[]>
      >(url, { headers: strapiConfig.headers, timeout: FETCH_TIMEOUT_MS })
      return response.data ?? []
    } catch {
      return []
    }
  }

  const fetchPagesWithLocaleFallback = async (): Promise<
    StrapiEventListPage[]
  > => {
    const pages = await fetchStrapiPages(locale.value)
    if (pages.length) return pages

    const allResults = await Promise.all(
      locales.value.map((l) => fetchStrapiPages(l.code))
    )
    return allResults.flat()
  }

  const fetchDynamicContent =
    async (): Promise<KftDynamicContentMetrics | null> => {
      const metricsRequest = getMetricsRequest(params.pageType, params)
      if (!metricsRequest) return null

      return fetchDynamicContentMetrics(
        metricsRequest.resource,
        metricsRequest.slug
      )
    }

  const redirectIfLocaleSlugMismatch = async (
    firstEntry: StrapiEventListPage
  ): Promise<boolean> => {
    const firstEntryLocale = strapiEventListPageGetters
      .getLocale(firstEntry)
      ?.toLowerCase()
    if (firstEntryLocale === locale.value) return false

    const localization = strapiEventListPageGetters.getLocalizationByLocale(
      firstEntry,
      locale.value
    )
    if (!localization) return false

    const entryLocale = strapiEventListPageGetters
      .getLocale(localization)
      ?.toLowerCase()
    const pagePath =
      strapiEventListPageGetters.getKonfettiPagePath(localization)
    const queryParams = route.fullPath.split('?')[1]
    let correctUrl = `/${entryLocale}${pagePath}/`

    if (queryParams && !correctUrl.includes('?')) {
      correctUrl += `?${queryParams}`
    }

    if (route.fullPath !== correctUrl) {
      setRedirectLogReason('locale-missing')
      await navigateTo({ path: correctUrl }, { redirectCode: 301 })
      return true
    }

    return false
  }

  const resolveLocaleEntry = (
    pages: StrapiEventListPage[]
  ): StrapiEventListPage | null => {
    return (
      pages.find((e) => e?.locale.toLowerCase() === locale.value) ??
      pages[0] ??
      null
    )
  }

  const applyDynamicContent = async (
    entry: StrapiEventListPage
  ): Promise<StrapiEventListPage> => {
    if (params.skipDynamicContent) return entry

    const metrics = await fetchDynamicContent()
    const metricsWithCurrentYear: KftDynamicContentMetrics = {
      ...(metrics ?? ({} as KftDynamicContentMetrics)),
      currentYear: new Date().getFullYear(),
    }

    return dynamicContentTransformer(
      entry,
      metricsWithCurrentYear,
      locale.value
    ) as StrapiEventListPage
  }

  const cacheKey = `resolved-elp-${params.pageType}-${params.slug}-${params.locationSlug}-${params.categorySlug}`

  const nuxt = useNuxtApp()

  return useAsyncData<
    StrapiEventListPage | null,
    Error,
    StrapiEventListPage | null
  >(
    cacheKey,
    async (): Promise<StrapiEventListPage | null> => {
      const pages = await fetchPagesWithLocaleFallback()
      if (!pages.length) return null

      const redirected = await redirectIfLocaleSlugMismatch(pages[0]!)
      if (redirected) return null

      const entry = resolveLocaleEntry(pages)
      if (!entry) return null

      return applyDynamicContent(entry)
    },
    {
      server: true,
      lazy: false,
      dedupe: 'defer',
      getCachedData(key) {
        return nuxt.isHydrating ? nuxt.payload.data[key] : nuxt.static.data[key]
      },
    }
  )
}
